import GroupIcon from '@mui/icons-material/Group';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import useBroadCast from 'shared/hooks/useBroadCast';
import DateFnsAdapter from '@date-io/date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';
import { Toast } from 'shared/helpers';

const dateFns = new DateFnsAdapter();

export interface IEmployee {
  _id: string;
  name: string;
  email: string;
  department: string;
  role: string;
}

export interface ITrianingAssignment {
  due_date: string;
  note: string;
  assigned_at: string;
  status: 'not_started' | 'in_progress' | 'completed';
  employee: IEmployee;
  assigneeId: string;
}

const Assignments: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { trainingId } = useParams();
  const [assignments, setAssignments] = useState<ITrianingAssignment[]>([]);
  const [assignmentId, setAssignmentId] = useState('');

  // API Calls
  const loadAssignments = (): void => {
    setIsLoading(true);
    TrainingPorgramsService.getAssignments(`${trainingId}`)
      .then((response) => {
        const { data } = response.data;
        setAssignments(data?.records);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };
  const resendAssignment = (id: string): void => {
    setAssignmentId(id);
    TrainingPorgramsService.resendAssignment(`${id}`, {})
      .then(() => {
        Toast.showInfoMessage('Mail resend successfully');
      })
      .catch(() => {})
      .finally(() => setAssignmentId(''));
  };

  // UI Actions
  const actionOnResend = (assignment: ITrianingAssignment): void => {
    resendAssignment(assignment.assigneeId);
  };

  // Hooks
  useEffect(() => {
    if (trainingId) {
      loadAssignments();
    }
  }, [trainingId]);

  useBroadCast({
    eventName: BROADCAST_MESSAGES.RELOAD_TRAINING_ASSIGNMENTS,
    onBroadCast: () => {
      loadAssignments();
    },
  });

  // UI Elements
  return (
    <Box
      sx={{
        background: 'white',
        padding: '10px 20px',
        borderRadius: '5px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <GroupIcon />
        <Typography sx={{ fontSize: '20px' }}>Training Assignments</Typography>
      </Box>

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Box className="mt-5">
          {assignments.map((assignment) => (
            <Box className="assignment">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography className="fw-6">
                  {assignment.employee.name}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                  {assignment.status === 'not_started' && (
                    <Chip
                      size="small"
                      label="Not Started"
                      icon={<AccessTimeIcon />}
                    />
                  )}
                  {assignment.status === 'in_progress' && (
                    <Chip
                      size="small"
                      label="Inprogress"
                      icon={<AccessTimeIcon />}
                    />
                  )}
                  {assignment.status === 'completed' && (
                    <Chip
                      size="small"
                      label="Completed"
                      icon={<CheckCircleOutlineIcon />}
                    />
                  )}
                  {assignment.status === 'not_started' && (
                    <AppPrimaryButtonOutlined
                      className="btn-extra-small"
                      loadingText="Sending"
                      title="Resend"
                      isLoading={assignmentId === assignment.assigneeId}
                      onClick={() => actionOnResend(assignment)}
                    />
                  )}
                </Stack>
              </Stack>
              <Typography className="description">
                {assignment.note || 'N/A'}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography className="">
                  Assigned:{' '}
                  {assignment.assigned_at
                    ? dateFns.date(assignment.assigned_at).toDateString()
                    : ''}
                </Typography>
                {assignment.due_date && (
                  <Typography className="">
                    • Due Date:{' '}
                    {dateFns.date(assignment.due_date).toDateString()}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      )}
      {!isLoading && assignments.length === 0 && (
        <Typography
          sx={{
            fontSize: '18px',
            color: '#7E7E7E',
            marginTop: '20px',
          }}
        >
          No Assignments
        </Typography>
      )}
    </Box>
  );
};

export default Assignments;
